import React, { useState, useEffect, FunctionComponent, CSSProperties, useRef } from 'react';
import { formatMoney, getInvoiceStatus } from '../../../utils';
import { DropdownItem } from './DropdownItem';

interface SearchInputProps<T> {
  jsonFileUrl?: string;
  data: Array<T>;
  displayKey: keyof T;
  onSelect?: (item: T | null) => void;
  isLoading?: boolean;
  placeholder?: string;
  classNames?: string;
  value?: string | null;
  selected?: T | null;
  handleChangeText?: (text: string) => void;
  style?: React.CSSProperties;
  image?: FunctionComponent<{ className?: string }>;
  clearIcon?: FunctionComponent<{ color?: string }>;
  emptyMessage?: string; // Empty message to be displayed
  error?: string;
  onClearError?: () => void;
  dropdownStyle?: React.CSSProperties;
  dropdownClassName?: string; // Custom class for the dropdown
  clearIconClass?: string;
  emptyLinks?: Array<{ label: string; onClick: () => void; icon?: React.ReactNode }>;
  emptyLinkStyle?: CSSProperties;
  specificSelectedValue?: boolean;
  icon?: React.ReactNode;
}

const SearchInput = <T extends { [key: string]: any }>({
  jsonFileUrl,
  data,
  displayKey,
  onSelect,
  isLoading,
  placeholder,
  classNames,
  selected,
  handleChangeText,
  style,
  clearIcon: ClearIcon,
  emptyMessage = 'No Result',
  error,
  onClearError,
  emptyLinks,
  emptyLinkStyle,
  dropdownClassName,
  dropdownStyle,
  clearIconClass,
  icon,
}: SearchInputProps<T>) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isItemSelected, setIsItemSelected] = useState(false);
  const [hasError, setHasError] = useState(false);
  const dropdownTimeout = useRef<number | undefined>(undefined);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [jsonData, setJsonData] = useState<T[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);


  useEffect(() => {
    if (jsonFileUrl) {
      fetch(jsonFileUrl)
        .then((response) => response.json())
        .then((json) => {
          setJsonData(json);
        })
        .catch((error) => {
          console.error('Error fetching JSON data:', error);
        });
    }
  }, [jsonFileUrl]);

  useEffect(() => {
    if (selected) {
      setSearchText(selected[displayKey]);
      setShowDropdown(false);
      setIsItemSelected(true);
      setHasError(false);
    } else {
      setSearchText('');
      setIsItemSelected(false);
      setHasError(false);
    }
  }, [selected, displayKey]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setHasError(true);
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      clearTimeout(dropdownTimeout.current);
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    handleChangeText && handleChangeText(event.target.value);
    setShowDropdown(event.target.value.length > 0);
    setHasError(false);
  };

  const handleDropdownClick = (item: T, event: React.MouseEvent) => {
    onClearError && onClearError();
    onSelect && onSelect(item);
    setSearchText(String(item[displayKey]));
    setShowDropdown(false);
    setIsItemSelected(true);
    event.stopPropagation();
  };

  const handleClearSelection = () => {
    setSearchText('');
    setShowDropdown(false);
    onSelect && onSelect(null);
    onClearError && onClearError();
  };
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const dropdownClicked = event.relatedTarget === dropdownRef.current;
    const inputClicked = event.relatedTarget === inputRef.current;

    if (!dropdownClicked && !inputClicked && showDropdown && searchText.length === 0) {
      setHasError(true);
    }
  };

  const handleInputClick = () => {
    if (hasError) {
      setHasError(false);
    }
  };

  const handleDropdownTimeout = () => {
    setHasError(true);
  };

  useEffect(() => {
    if (showDropdown && searchText.length > 0) {
      dropdownTimeout.current = window.setTimeout(handleDropdownTimeout, 10000);
    } else {
      clearTimeout(dropdownTimeout.current);
    }
  }, [showDropdown, searchText])

  const renderDropdownItems = () => {
    const itemsToRender = jsonData.length > 0 ? jsonData.filter(item => {
      const itemValue = String(item[displayKey]).toLowerCase();
      const searchTextLower = searchText.toLowerCase();
      return itemValue.includes(searchTextLower);
    }) : data.filter(item => {
      const itemValue = String(item[displayKey]).toLowerCase();
      const searchTextLower = searchText.toLowerCase();
      return itemValue.includes(searchTextLower);
    });

    return (
      <div>
        {error && <div className="text-red-500 text-sm">{error}</div>}
        {isLoading ? (
          <div className="flex items-start justify-start py-4">
            <span className="text-gray-500">Loading...</span>
          </div>
        ) : (
          <ul className="space-y-2">
            {itemsToRender.map((item, i) => (
              <li
                key={i}
                className={`hover:bg-blue-200 cursor-pointer flex items-center px-4 py-2 focus:outline-none focus:border-[#138EFF] focus:ring-2 focus:ring-[#138EFF]`}
                onClick={(event) => handleDropdownClick(item, event)}
              >
                <DropdownItem item={item} displayKey={displayKey} onClick={handleDropdownClick} />
              </li>
            ))}
          </ul>
        )}
        {!isLoading && itemsToRender.length === 0 && (
          <div>
            <span className="text-gray-500 italic ml-[1em]">{emptyMessage}</span>
            {emptyLinks && emptyLinks.length > 0 && (
              <ul>
                {emptyLinks.map(({ label, onClick, icon }, index) => (
                  <li key={index} style={emptyLinkStyle}>
                    <a
                      href="#"
                      className="text-[#044E97] flex items-center space-x-1 mt-[2em] ml-[1em] font-semi-bold text-[16px]"
                      onClick={(event) => {
                        event.stopPropagation();
                        onClick();
                      }}
                    >
                      <span>{label}</span>
                      {icon && <span>{icon}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  const inputClassNames = selected
    ? `${classNames ||
        'border border-[#138EFF]  placeholder:text-[15.25px] h-[48px] rounded-md p-2'} focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal ${
        selected ? 'bg-[#E3EFFC] pl-5 border-[#138EFF]' : ''
      }`
    : `${classNames ||
        'border border-gray-300 placeholder:text-[15.25px] h-[48px] rounded-md p-2'} focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal`;

  const inputClassName = `${inputClassNames}${hasError ? ' border border-red-500' : ''}`;

  return (
    <div className="relative" style={style}>
       {icon && (
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </div>
      )}
    <input
  type="text"
  placeholder={placeholder}
  value={searchText}
  onChange={handleInputChange}
  className={`${
    icon ? 'pl-[4em] lg:pl-[2.5em] xl:pl-[3em] biglg:pl-[2.5em] md:pl-[3em]' : '' 
  }  ${inputClassName}`}
  disabled={Boolean(selected)}
  onBlur={handleBlur}
  onClick={handleInputClick}
  ref={inputRef}
/>


     {selected && (
        <button onClick={handleClearSelection} className={`absolute right-0 top-0 mr-[5em] mt-[1.2em] xl:mt-[1.3em]  md:mr-[1em] md:mt-[1em] lg:mr-[-1em] ${clearIconClass}`}>
          {ClearIcon ? <ClearIcon color="#FF0000" /> : 'x'}
        </button>
      )}
      {showDropdown && (
        <div
          className={`absolute z-10 bg-white rounded-sm border  ${
            hasError ? 'border-red-500' : 'border-gray-300'
          }  mt-[3px] ${dropdownClassName || ''}`}
          style={{ ...dropdownStyle }}
          ref={dropdownRef}
        >
          {renderDropdownItems()}
        </div>
      )}
    </div>
  );
};

export { SearchInput };
